import type { VisionResult } from "@studystraight/shared-types";
import { useCallback, useMemo, useRef } from "react";

import { computeDebugSample, type DebugSample } from "./debugMetrics.js";

/** §25 — giữ 60 giây gần nhất. Ở 12 FPS là ~720 sample, mỗi sample ~15 số → vài trăm KB. */
const DEFAULT_WINDOW_MS = 60_000;

export interface DebugRecorder {
  /** Gọi ở MỌI frame inference (không throttle) — đây là dữ liệu để tune, không phải để hiển thị. */
  push: (vision: VisionResult, inferenceMs: number) => void;
  snapshot: () => DebugSample[];
  latest: () => DebugSample | null;
  clear: () => void;
  /** Frame gần nhất, giữ nguyên landmark — dùng để vẽ skeleton. */
  latestVision: () => VisionResult | null;
}

/**
 * Ring buffer bằng `useRef`, **không** qua React state.
 *
 * Nếu để trong state, mỗi frame inference (8–15/giây) sẽ tạo một re-render của cả cây component
 * — vi phạm §31 (UI update 2–5 FPS) và làm máy nóng vì render chứ không vì inference.
 */
export function useDebugRecorder(windowMs = DEFAULT_WINDOW_MS): DebugRecorder {
  const samples = useRef<DebugSample[]>([]);
  const lastVision = useRef<VisionResult | null>(null);

  const push = useCallback(
    (vision: VisionResult, inferenceMs: number) => {
      lastVision.current = vision;
      const sample = computeDebugSample(vision, inferenceMs);
      samples.current.push(sample);
      const cutoff = sample.t - windowMs;
      // Cắt theo timestamp của frame, không theo số lượng: FPS tự hạ khi máy yếu (§31), nên
      // "720 sample" không còn là 60 giây nữa.
      while (samples.current.length > 0 && (samples.current[0]?.t ?? 0) < cutoff) {
        samples.current.shift();
      }
    },
    [windowMs],
  );

  return useMemo<DebugRecorder>(
    () => ({
      push,
      snapshot: () => [...samples.current],
      latest: () => samples.current[samples.current.length - 1] ?? null,
      latestVision: () => lastVision.current,
      clear: () => {
        samples.current = [];
        lastVision.current = null;
      },
    }),
    [push],
  );
}
